import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdSearch, MdFileDownload, MdRefresh } from 'react-icons/md';
import '../master/MasterData.css';

interface StockItem {
  itemCode: string;
  itemName: string;
  itemType: 'RAW_MATERIAL' | 'FINISHED_GOODS' | 'WORK_IN_PROGRESS';
  unit: string;
  quantity: number;
  unitCost: number;
  totalValue: number;
  location: string;
  minStock: number;
  maxStock: number;
  lastUpdated: string;
}

const StockStatus: React.FC = () => {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('ALL');

  useEffect(() => {
    // TODO: Fetch stock status from API
    setStockItems([
      {
        itemCode: 'RM-001',
        itemName: 'Polyurethane Foam - High Density',
        itemType: 'RAW_MATERIAL',
        unit: 'kg',
        quantity: 5000,
        unitCost: 150,
        totalValue: 750000,
        location: 'Warehouse A - Raw Materials',
        minStock: 1000,
        maxStock: 10000,
        lastUpdated: '2025-02-28'
      },
      {
        itemCode: 'RM-002',
        itemName: 'Fabric - Premium Grade',
        itemType: 'RAW_MATERIAL',
        unit: 'meters',
        quantity: 3000,
        unitCost: 200,
        totalValue: 600000,
        location: 'Warehouse A - Raw Materials',
        minStock: 500,
        maxStock: 5000,
        lastUpdated: '2025-02-28'
      },
      {
        itemCode: 'FG-001',
        itemName: 'Executive Office Chair - Black',
        itemType: 'FINISHED_GOODS',
        unit: 'pcs',
        quantity: 150,
        unitCost: 3000,
        totalValue: 450000,
        location: 'Warehouse B - Finished Goods',
        minStock: 50,
        maxStock: 300,
        lastUpdated: '2025-02-28'
      },
      {
        itemCode: 'FG-002',
        itemName: '3-Seater Sofa - Grey',
        itemType: 'FINISHED_GOODS',
        unit: 'pcs',
        quantity: 75,
        unitCost: 15000,
        totalValue: 1125000,
        location: 'Warehouse B - Finished Goods',
        minStock: 20,
        maxStock: 100,
        lastUpdated: '2025-02-28'
      },
      {
        itemCode: 'WIP-001',
        itemName: 'Office Chair Frame Assembly',
        itemType: 'WORK_IN_PROGRESS',
        unit: 'pcs',
        quantity: 50,
        unitCost: 1500,
        totalValue: 75000,
        location: 'Production Floor',
        minStock: 0,
        maxStock: 100,
        lastUpdated: '2025-02-28'
      }
    ]);
  }, []);

  const filteredItems = stockItems.filter(item => {
    const matchesSearch =
      item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'ALL' || item.itemType === filterType;

    return matchesSearch && matchesType;
  });

  const totals = {
    totalValue: filteredItems.reduce((sum, item) => sum + item.totalValue, 0),
    totalItems: filteredItems.length
  };

  const getStockStatusBadge = (item: StockItem) => {
    if (item.quantity <= item.minStock) {
      return <span className="badge badge-danger">LOW STOCK</span>;
    } else if (item.quantity >= item.maxStock) {
      return <span className="badge badge-warning">OVERSTOCK</span>;
    } else {
      return <span className="badge badge-success">NORMAL</span>;
    }
  };

  const getItemTypeBadge = (type: string) => {
    switch (type) {
      case 'RAW_MATERIAL': return <span className="badge badge-info">RAW MATERIAL</span>;
      case 'FINISHED_GOODS': return <span className="badge badge-success">FINISHED GOODS</span>;
      case 'WORK_IN_PROGRESS': return <span className="badge badge-warning">WIP</span>;
      default: return <span className="badge badge-secondary">{type}</span>;
    }
  };

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handleRefresh = () => {
    alert('Refreshing stock data...');
    // TODO: Refresh stock data from API
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Stock Status</h2>
            <p className="subtitle">Monitor current inventory levels and values</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <MdRefresh /> Refresh
            </button>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📦</div>
            <div className="summary-content">
              <p className="summary-label">Total Items</p>
              <p className="summary-value">{totals.totalItems}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <p className="summary-label">Total Inventory Value</p>
              <p className="summary-value">₱{totals.totalValue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterType === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterType('ALL')}
              >
                All Items
              </button>
              <button
                className={`filter-tab ${filterType === 'RAW_MATERIAL' ? 'active' : ''}`}
                onClick={() => setFilterType('RAW_MATERIAL')}
              >
                Raw Materials
              </button>
              <button
                className={`filter-tab ${filterType === 'FINISHED_GOODS' ? 'active' : ''}`}
                onClick={() => setFilterType('FINISHED_GOODS')}
              >
                Finished Goods
              </button>
              <button
                className={`filter-tab ${filterType === 'WORK_IN_PROGRESS' ? 'active' : ''}`}
                onClick={() => setFilterType('WORK_IN_PROGRESS')}
              >
                WIP
              </button>
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Unit Cost</th>
                  <th>Total Value</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, index) => (
                  <tr key={index}>
                    <td className="code">{item.itemCode}</td>
                    <td className="name">{item.itemName}</td>
                    <td>{getItemTypeBadge(item.itemType)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {item.quantity.toLocaleString()}
                    </td>
                    <td>{item.unit}</td>
                    <td className="amount">₱{item.unitCost.toLocaleString()}</td>
                    <td className="amount">₱{item.totalValue.toLocaleString()}</td>
                    <td>{item.location}</td>
                    <td>{getStockStatusBadge(item)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td colSpan={6} style={{ textAlign: 'right' }}>TOTAL VALUE:</td>
                  <td className="amount">₱{totals.totalValue.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>

            {filteredItems.length === 0 && (
              <div className="no-data">
                <p>No stock items found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default StockStatus;
